import { createContext, FC, useContext, useState } from 'react'
import { ResourceType } from '../../../../constants'

interface Specialist {
  name: string
  namePlural: string
  title: string
  titlePlural: string
}

enum SpecialistType {
  AUDIOLOGIST = 'AUDIOLOGIST',
  CABIN = 'CABIN',
  CONSULTANT = 'CONSULTANT',
  CUSTOMER_SUPPORT = 'CUSTOMER_SUPPORT',
  FITTER = 'FITTER',
  SPECIALIST = 'SPECIALIST',
  VIDEO_CARE_CALL_SPECIALIST = 'VIDEO_CARE_CALL_SPECIALIST',
}

type ResourceTypesExtended = ResourceType | 'SPECIALIST'

const resourceTypeToSpecialistTypeMap: Record<ResourceTypesExtended, SpecialistType> = {
  CABIN: SpecialistType.CABIN,
  CONSULTANT: SpecialistType.CONSULTANT,
  CUSTOMER_SUPPORT: SpecialistType.CUSTOMER_SUPPORT,
  FITTER: SpecialistType.FITTER,
  FITTER_INTERNAL: SpecialistType.FITTER,
  SPECIALIST: SpecialistType.SPECIALIST,
  VIDEO_CARE_CALL_SPECIALIST: SpecialistType.VIDEO_CARE_CALL_SPECIALIST,
}

interface UseHookContext {
  setSpecialistType: (type: ResourceTypesExtended) => void
  specialist: Specialist
}

const defaultSpecialist: Specialist = {
  name: 'provider',
  namePlural: 'providers',
  title: 'common.label.specialistType.fitter.title',
  titlePlural: 'common.label.specialistType.fitter.titlePlural',
}

const context = createContext({
  setSpecialistType: () => undefined,
  specialist: defaultSpecialist,
} as UseHookContext)

const { Provider } = context

const specialistTypeToCopyMap: Record<SpecialistType, Specialist> = {
  AUDIOLOGIST: defaultSpecialist,
  CABIN: {
    name: 'cabin',
    namePlural: 'cabins',
    title: 'common.label.resource.cabin',
    titlePlural: 'common.label.resource.cabins',
  },
  CONSULTANT: defaultSpecialist,
  CUSTOMER_SUPPORT: {
    name: 'Support specialist',
    namePlural: 'Support specialists',
    title: 'Support Specialist',
    titlePlural: 'Support Specialists',
  },
  FITTER: defaultSpecialist,
  SPECIALIST: {
    name: 'specialist',
    namePlural: 'specialists',
    title: 'common.label.specialistType.specialist.title',
    titlePlural: 'common.label.specialistType.specialist.titlePlural',
  },
  VIDEO_CARE_CALL_SPECIALIST: {
    name: 'VCC specialist',
    namePlural: 'VCC specialists',
    title: 'VCC Specialist',
    titlePlural: 'VCC Specialists',
  },
}

const SpecialistProvider: FC = ({ children }) => {
  const [specialist, setSpecialist] = useState<Specialist>(defaultSpecialist)
  const setSpecialistType = (type: ResourceTypesExtended) => setSpecialist(specialistTypeToCopyMap[resourceTypeToSpecialistTypeMap[type]])

  const contextValue = {
    setSpecialistType,
    specialist,
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useSpecialist = () => useContext(context)

export { SpecialistProvider, useSpecialist }
